import { useCallback, useState } from 'react';
import { UpdateContentPagesRequestDto } from '@tiendanube/common';
import { useGetThemesConfigQuery } from 'domains/Online/Themes/themesApi';
import { DesignType } from '../../types';
import useCustomPageThemeEditorLink from '../useCustomPageThemeEditorLink';

export default function useDesignEditorLaunchModal({
  pageId,
  submitFn,
  onSubmitSuccess,
}: {
  pageId?: string;
  submitFn?: (
    data: UpdateContentPagesRequestDto,
  ) => Promise<string | undefined>;
  onSubmitSuccess?: (
    pageId: string,
    designType: DesignType,
    data: UpdateContentPagesRequestDto,
  ) => void;
} = {}) {
  const [showDesignModal, setShowDesignModal] = useState(false);
  const { goToThemeEditor } = useCustomPageThemeEditorLink();
  const { data: config } = useGetThemesConfigQuery();

  const currentTheme = config?.currentTheme.code;
  const draftTheme = config?.draft?.code;

  const handleOpenEditDesignModal = useCallback(async () => {
    if (pageId && currentTheme && !draftTheme) {
      await goToThemeEditor('current', pageId);
    } else {
      setShowDesignModal(true);
    }
  }, [pageId, currentTheme, draftTheme, goToThemeEditor]);

  const handleCloseEditDesignModal = useCallback(() => {
    setShowDesignModal(false);
  }, []);

  const handleDesignEditModalSubmit = useCallback(
    async (data: UpdateContentPagesRequestDto, designType: DesignType) => {
      try {
        const createdId = submitFn ? await submitFn(data) : data.id;

        if (createdId) {
          setShowDesignModal(false);
          await goToThemeEditor(designType, createdId);
          onSubmitSuccess?.(createdId, designType, data);
        }
      } catch (error) {
        // Error is displayed by a toast in the page based on the query status
      }
    },
    [submitFn, goToThemeEditor, onSubmitSuccess],
  );

  return {
    showDesignModal: showDesignModal && !!currentTheme,
    draftTheme: draftTheme,
    currentTheme: currentTheme,
    themesConfig: config,
    handleOpenEditDesignModal,
    handleCloseEditDesignModal,
    handleDesignEditModalSubmit,
  };
}
